// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

import type { HoleShapeFeature } from './useHoleFeatures';
import { parsePolygonRing } from './useCourseAnalysis';

/**
 * What an evaluation scored, polygon by polygon, with the matcher's verdict on each.
 *
 * The models page shows a precision of 0.18 and stops. This is the rest of that sentence: the
 * three bunkers the model found that OSM agrees with, the fourteen it called bunkers that OSM
 * does not have, and the four OSM bunkers it never saw — drawn on the hole, so the number can be
 * looked at rather than imagined. The classical detector's evaluation sits beside the model's,
 * which is the comparison the whole exercise exists for.
 */

/** One evaluation's polygons on this course: one detector, one kind. */
export type EvaluationRun = {
  evaluationId: string;
  /** classical | model. */
  detector: string;
  modelVersion: string | null;
  modelNickname: string | null;
  kind: string;
  evaluatedAt: string | null;
  precision: number;
  recall: number;
  f1: number;
  counts: { matched: number; missed: number; falsePositive: number };
};

export const MATCHED = 'matched';
export const MISSED = 'missed';
export const FALSE_POSITIVE = 'false-positive';

/**
 * The source string evaluation shapes carry. Its own value, so the modal's filter can switch
 * them as a group and nothing mistakes a scored polygon for a published one or a candidate: a
 * false positive here is a fact about the detector, not a proposal for the course.
 */
export const EVALUATION_SOURCE = 'evaluation';

/** What tells two evaluations apart for drawing: the detector, and for a model its checkpoint. */
export const evaluationKey = (run: Pick<EvaluationRun, 'detector' | 'modelVersion'>): string =>
  run.modelVersion ? `${run.detector}:${run.modelVersion}` : run.detector;

type Row = Record<string, unknown>;

const str = (row: Row, ...keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return '';
};

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

/** Maps the BFF's `/api/geodata/courses/{id}/evaluation-features` body. Exported for direct unit testing. */
export const mapEvaluationFeatures = (body: unknown): { runs: EvaluationRun[]; shapes: HoleShapeFeature[] } => {
  if (!Array.isArray(body)) return { runs: [], shapes: [] };
  const runs: EvaluationRun[] = [];
  const shapes: HoleShapeFeature[] = [];

  for (const row of body as Row[]) {
    const evaluationId = str(row, 'evaluationId', 'EvaluationId');
    const detector = str(row, 'detector', 'Detector') || 'classical';
    const modelVersion = str(row, 'modelVersion', 'ModelVersion') || null;
    if (!evaluationId) continue;
    const key = evaluationKey({ detector, modelVersion });
    const counts = { matched: 0, missed: 0, falsePositive: 0 };

    const rawFeatures = row.features ?? row.Features;
    for (const feature of Array.isArray(rawFeatures) ? (rawFeatures as Row[]) : []) {
      const id = str(feature, 'id', 'Id');
      const kind = str(feature, 'kind', 'Kind');
      const outcome = str(feature, 'outcome', 'Outcome');
      const ring = parsePolygonRing(str(feature, 'polygonGeoJson', 'PolygonGeoJson') || null);
      if (!id || !kind || !ring) continue;
      if (outcome === MATCHED) counts.matched++;
      else if (outcome === MISSED) counts.missed++;
      else if (outcome === FALSE_POSITIVE) counts.falsePositive++;
      shapes.push({
        id,
        kind,
        ring,
        holeNumber: num(feature, 'holeNumber', 'HoleNumber'),
        name: null,
        source: EVALUATION_SOURCE,
        sourceProvider: key,
        outcome,
        confidence: 0,
        sampleCount: 0,
      });
    }

    runs.push({
      evaluationId,
      detector,
      modelVersion,
      modelNickname: str(row, 'modelNickname', 'ModelNickname') || null,
      kind: str(row, 'kind', 'Kind'),
      evaluatedAt: str(row, 'evaluatedAt', 'EvaluatedAt') || null,
      precision: num(row, 'precision', 'Precision'),
      recall: num(row, 'recall', 'Recall'),
      f1: num(row, 'f1', 'F1'),
      counts,
    });
  }

  return { runs, shapes };
};

export function useEvaluationFeatures(
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  /** Fetched only where the admin tools are shown — the same gate as the analysis candidates. */
  enabled: boolean
): { runs: EvaluationRun[]; shapes: HoleShapeFeature[] } {
  const [state, setState] = useState<{ runs: EvaluationRun[]; shapes: HoleShapeFeature[] }>({ runs: [], shapes: [] });

  useEffect(() => {
    if (!enabled || !token || !courseId) {
      setState({ runs: [], shapes: [] });
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/evaluation-features`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        // An older backend without the route, or a caller without the role: nothing to draw.
        if (!response.ok) {
          setState({ runs: [], shapes: [] });
          return;
        }
        const body = await response.json();
        if (!cancelled) setState(mapEvaluationFeatures(body));
      } catch {
        if (!cancelled) setState({ runs: [], shapes: [] });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, token, courseId, enabled]);

  return state;
}
